/**
 * `AttachmentImage` — the Tiptap Image node, taught to (1) render `attachment:<id>` references,
 * (2) be drag-resizable, and (3) offer right-click delete.
 *
 * Attachments & media track: note bodies store a logical `![alt](attachment:<id>)` reference
 * (not base64 bytes). The node view resolves an `attachment:` src to an authed object URL at
 * DISPLAY time — while the node's `src` ATTRIBUTE stays `attachment:<id>`, so `getHTML()` →
 * turndown serialises the canonical reference back into markdown unchanged.
 *
 * Resize: a `width` attribute (pixels) persists the user's drag-resize. Plain images round-trip
 * as `![alt](attachment:<id>)`; a RESIZED image round-trips as raw `<img … width=…>` HTML (the
 * `imageWithSize` turndown keep-rule in markdownRoundtrip.ts — markdown has no width syntax).
 *
 * `allowBase64: true` keeps legacy `data:` images rendering through the migration off inline bytes.
 */

import Image from '@tiptap/extension-image';
import { Plugin, NodeSelection } from '@tiptap/pm/state';
import { resolveAttachmentUrl } from '@/api';
import { isDesktopApp } from '@/desktop/isDesktopApp';
import { copyImagePng } from '@/desktop/desktopIpc';

const ATTACHMENT_PREFIX = 'attachment:';
const MIN_WIDTH_PX = 48;

/**
 * Copy an editor image to the system clipboard as real image BYTES (so it can be pasted into
 * other apps or back into the editor) — 2026-06-29, dual-path rev.
 *
 * Resolves an `attachment:<id>` reference to its authed object URL, fetches the bytes, and
 * re-encodes to PNG. Then:
 *   - **Desktop shell (WebView2):** hands the PNG bytes to the OS clipboard NATIVELY via the
 *     `copy_image_png` Rust IPC command (the clipboard-manager plugin). WebView2's
 *     `navigator.clipboard.write([ClipboardItem image/png])` silently drops image bytes — text
 *     lands, images don't — so prior web-clipboard fixes never actually worked in the desktop app.
 *   - **Web build (Chrome/Edge):** uses the async Clipboard API, which handles `image/png`.
 *
 * Throws on failure so the caller can surface WHY (the menu shows the message), rather than failing
 * silently.
 */
async function copyImageToClipboard(src: string): Promise<void> {
  // Resolve attachment refs + fetch the actual bytes, re-encoding to PNG (shared by both paths).
  let url = src;
  if (src.startsWith(ATTACHMENT_PREFIX)) {
    url = await resolveAttachmentUrl(src.slice(ATTACHMENT_PREFIX.length));
  }
  const resp = await fetch(url);
  let blob = await resp.blob();
  if (blob.type !== 'image/png') {
    blob = await blobToPng(blob);
  }

  // Desktop: route to the native clipboard — WebView2's web clipboard drops image bytes.
  if (isDesktopApp()) {
    const png = new Uint8Array(await blob.arrayBuffer());
    await copyImagePng(png);
    return;
  }

  // Web: the async Clipboard API handles image/png in Chrome/Edge.
  if (
    typeof navigator === 'undefined' ||
    navigator.clipboard === undefined ||
    typeof navigator.clipboard.write !== 'function' ||
    typeof ClipboardItem === 'undefined'
  ) {
    throw new Error('Clipboard image-copy is not available in this browser/view.');
  }
  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
}

/** Redraw an image blob to a PNG blob via a canvas — the universally clipboard-writable format. */
async function blobToPng(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('no 2d context for image copy');
  ctx.drawImage(bitmap, 0, 0);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
}

export const AttachmentImage = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      // Rendered/persisted width in CSS pixels (number). `null` = natural size.
      width: {
        default: null,
        parseHTML: (el) => {
          const attr = el.getAttribute('width');
          if (attr) return parseInt(attr, 10) || null;
          const styleW = (el as HTMLElement).style?.width;
          return styleW ? parseInt(styleW, 10) || null : null;
        },
        renderHTML: (attrs) => (attrs.width ? { width: String(attrs.width) } : {}),
      },
    };
  },

  addNodeView() {
    return ({ editor, node, getPos }) => {
      const wrap = document.createElement('span');
      wrap.className = 'ed-attachment-wrap';

      const img = document.createElement('img');
      img.className = 'ed-attachment-img';
      img.draggable = false;
      const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
      const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
      if (alt) img.alt = alt;
      if (typeof node.attrs.title === 'string' && node.attrs.title) img.title = node.attrs.title;
      if (typeof node.attrs.width === 'number' && node.attrs.width > 0) {
        img.style.width = `${node.attrs.width}px`;
      }

      // Alignment (2026-06-23): the toolbar's text-align buttons set `textAlign` on the image
      // node (TextAlign now includes 'image' in its types). The wrap is inline-block by default;
      // to center/right a block image we switch it to a fit-content block with auto margins —
      // which also keeps the resize handle pinned to the image's corner (it's a child of wrap).
      const align = typeof node.attrs.textAlign === 'string' ? node.attrs.textAlign : null;
      if (align === 'center') {
        wrap.style.display = 'block';
        wrap.style.width = 'fit-content';
        wrap.style.marginLeft = 'auto';
        wrap.style.marginRight = 'auto';
      } else if (align === 'right') {
        wrap.style.display = 'block';
        wrap.style.width = 'fit-content';
        wrap.style.marginLeft = 'auto';
        wrap.style.marginRight = '0';
      }
      // 'left' / 'justify' / null → leave the CSS default (inline-block, flows with text).

      if (src.startsWith(ATTACHMENT_PREFIX)) {
        const id = src.slice(ATTACHMENT_PREFIX.length);
        img.dataset.attachmentId = id;
        img.classList.add('ed-attachment-img--loading');
        // The object URL is SHARED + cached by resolveAttachmentUrl (one per id, session-lived);
        // we deliberately do NOT revoke it on teardown (Tiptap recreates node views constantly,
        // and revoking would kill the cached URL → broken-<img> for every later view).
        resolveAttachmentUrl(id)
          .then((url) => {
            img.src = url;
            img.classList.remove('ed-attachment-img--loading');
          })
          .catch(() => {
            img.classList.remove('ed-attachment-img--loading');
            img.classList.add('ed-attachment-img--error');
            img.alt = (alt ? `${alt} ` : '') + '(attachment unavailable)';
          });
      } else {
        img.src = src; // data: or http(s): — render directly (legacy base64 / absolute URL).
      }
      wrap.appendChild(img);

      // --- drag-to-resize (bottom-right handle) -------------------------------------------
      const handle = document.createElement('span');
      handle.className = 'ed-attachment-resize';
      handle.setAttribute('aria-hidden', 'true');
      wrap.appendChild(handle);

      handle.addEventListener('pointerdown', (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startW = img.getBoundingClientRect().width;
        const maxW = wrap.parentElement?.getBoundingClientRect().width;
        const onMove = (ev: PointerEvent): void => {
          let w = Math.round(startW + (ev.clientX - startX));
          w = Math.max(MIN_WIDTH_PX, w);
          if (maxW) w = Math.min(w, Math.round(maxW));
          img.style.width = `${w}px`;
        };
        const onUp = (ev: PointerEvent): void => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          let w = Math.round(startW + (ev.clientX - startX));
          w = Math.max(MIN_WIDTH_PX, w);
          if (maxW) w = Math.min(w, Math.round(maxW));
          // Commit the width to the node so it persists (and round-trips to markdown HTML).
          if (typeof getPos === 'function') {
            const pos = getPos();
            if (typeof pos === 'number') {
              editor.view.dispatch(
                editor.view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, width: w }),
              );
            }
          }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      });

      // --- right-click → delete -----------------------------------------------------------
      const deleteNode = (): void => {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (typeof pos !== 'number') return;
        editor.view.dispatch(editor.view.state.tr.delete(pos, pos + node.nodeSize));
        editor.view.focus();
      };

      wrap.addEventListener('contextmenu', (e: MouseEvent) => {
        e.preventDefault();
        document.querySelectorAll('.ed-attachment-menu').forEach((m) => m.remove());
        const menu = document.createElement('div');
        menu.className = 'ed-attachment-menu';
        menu.style.left = `${e.clientX}px`;
        menu.style.top = `${e.clientY}px`;
        // Copy image → writes the actual bytes to the clipboard (paste into other apps / back in).
        const copy = document.createElement('button');
        copy.type = 'button';
        copy.className = 'ed-attachment-menu__item';
        copy.textContent = 'Copy image';
        copy.addEventListener('click', () => {
          copy.disabled = true;
          copy.textContent = 'Copying…';
          copyImageToClipboard(src)
            .then(() => {
              copy.textContent = 'Copied ✓';
              window.setTimeout(() => menu.remove(), 600);
            })
            .catch((err: unknown) => {
              // Surface WHY, in place, so a clipboard rejection is visible (not a silent no-op).
              copy.textContent = 'Copy failed';
              copy.title = err instanceof Error ? err.message : String(err);
              copy.disabled = false;
            });
        });
        menu.appendChild(copy);
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'ed-attachment-menu__item ed-attachment-menu__item--danger';
        del.textContent = 'Delete image';
        del.addEventListener('click', () => {
          deleteNode();
          menu.remove();
        });
        menu.appendChild(del);
        document.body.appendChild(menu);
        const close = (ev: Event): void => {
          if (ev.target instanceof Node && menu.contains(ev.target)) return;
          menu.remove();
          window.removeEventListener('pointerdown', close, true);
          window.removeEventListener('blur', close, true);
        };
        // Defer so this same click doesn't immediately close the menu.
        window.setTimeout(() => {
          window.addEventListener('pointerdown', close, true);
          window.addEventListener('blur', close, true);
        }, 0);
      });

      return {
        dom: wrap,
        // Leaf node — its DOM (img src/style we mutate ourselves) is not editable content, so
        // tell ProseMirror to ignore our mutations rather than try to re-parse them.
        ignoreMutation: () => true,
      };
    };
  },

  addProseMirrorPlugins() {
    const nodeName = this.name;
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        props: {
          handleDOMEvents: {
            // Ctrl/Cmd+C with an image selected → copy the image BYTES to the clipboard (not the
            // `<img>` HTML), so it pastes as a real image elsewhere. Mirrors the right-click
            // "Copy image". Cut + paste are left to their existing behaviour.
            copy: (view, event) => {
              const sel = view.state.selection;
              // Find the image to copy: either a clicked image (NodeSelection) or a drag/range
              // selection that contains exactly one image node ("drag over the image + Ctrl/Cmd+C").
              let imgSrc = '';
              if (sel instanceof NodeSelection && sel.node.type.name === nodeName) {
                imgSrc = typeof sel.node.attrs.src === 'string' ? sel.node.attrs.src : '';
              } else if (!sel.empty) {
                const found: string[] = [];
                view.state.doc.nodesBetween(sel.from, sel.to, (n) => {
                  if (n.type.name === nodeName && typeof n.attrs.src === 'string') found.push(n.attrs.src);
                });
                if (found.length === 1) imgSrc = found[0];
              }
              if (imgSrc !== '') {
                event.preventDefault();
                void copyImageToClipboard(imgSrc).catch(() => {
                  /* a copy-event clipboard write can be blocked; the right-click "Copy image"
                     menu is the reliable path and surfaces any error */
                });
                return true;
              }
              return false;
            },
          },
        },
      }),
    ];
  },
}).configure({ allowBase64: true });
